import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation } from 'react-router-dom';

import { AnalysisPhases } from './constant';
import CodeReviewCard from 'components/PerformanceReview4c/Solution';
import { useNavigateWithClient } from 'hooks';
import useCodingStore from './CodingStore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    height: '100vh',
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  backgroundVideo: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    objectFit: 'cover',
    zIndex: -1,
    pointerEvents: 'none',
  },
  container: {
    height: '100%',
    width: '90%',
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(23),
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  analysisContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(16, 0, 8, 0),
    flexDirection: 'column',
  },
  phasesContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(11),
    flexDirection: 'column',
  },
  topBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  phases: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 763,
  },
  phase : {
    alignItems : "center", 
    display : "flex", 
    flexDirection : "column", 
    gap : "12px"
  },
  progressContainer: {
    width: 647,
    position: 'relative',
    height: 16,
    left: 15,
  },
  progressBarTrack: {
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '100%',
    height: 12,
    backgroundColor: '#F0F5FA',
    borderRadius: 4,
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  progressBarFill: {
    position: 'absolute',
    top: '50%',
    left: 0,
    height: 12,
    backgroundColor: theme.palette.primary['clr-400'],
    borderRadius: 4,
    transform: 'translateY(-50%)',
    zIndex: 2,
  },
  progressDot: {
    position: 'absolute',
    top: '50%',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: '#BFCAD9',
    border: '2px solid #BFCAD9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
    transform: 'translate(-50%, -50%)',
  },
  activeDot: {
    border: `2px solid ${theme.palette.primary['clr-400']}`,
    backgroundColor: theme.palette.primary['clr-400'],
  },
  innerDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.shades['clr-white-900'],
  },
  messageBox: {
    width: '100%',
    maxWidth: 500,
    height: 180,
    position: 'relative',
    overflow: 'hidden',
    left: 65,
    display: 'flex',
    justifyContent: 'center',
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    transition: 'transform 0.5s ease-in-out',
    position: 'absolute',
    top: '5%',
    width: '100%',
  },
  messageItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    height: 36,
    opacity: 0.5,
    transition: 'opacity 0.3s ease',
  },
  activeMessageItem: {
    opacity: 1,
  },
  completedMessageItem: {
    opacity: 0.7,
  },
  loadingIcon: {
    color: theme.palette.primary['clr-400'],
    width: '24px !important',
    height: '24px !important',
  },
  checkIcon: {
    color: theme.palette.success['clr-500'],
    width: '28px !important',
    height: '28px !important',
  },
  upcomingIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '2px solid #BFCAD9',
  },
  codeContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    padding: theme.spacing(10, 16, 13, 16),
    backgroundImage: 'url("https://assets.languify.in/images/bgEngagementCoding.png")',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    boxShadow: '0px 2px 12px 0px #1151A61A inset',
    position: 'relative',
  },
}));

const dotPositions = [0, 36.1, 71.7, 100];
const progressFillWidths = [0, 36.1, 71.7, 100];

const Phases = ({ phases, activePhaseIndex }) => {
  const classes = useStyles();
  return (
    <Box className={classes.phases}>
      {phases.map((phase, idx) => {
        const isCompleted = idx < activePhaseIndex;
        const isActive = idx === activePhaseIndex;

        return (
          <Box 
            key={phase.name} 
            className={classes.phase} 
            style={{ opacity: idx > activePhaseIndex ? 0.25 : 1 }}
          >
            <img src={phase.images} alt="phase" width={72} />
            <Typography 
              variant="h6-medium" 
              color={isActive ? 'primary.main' : isCompleted ? 'black' : 'rgba(0,0,0,0.4)'}
            >
              {phase.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const ProgressBar = ({ activePhaseIndex }) => {
  const classes = useStyles();
  const progressBarWidth = progressFillWidths[activePhaseIndex];

  return (
    <Box className={classes.progressContainer}>
      <Box className={classes.progressBarTrack} />
      <Box className={classes.progressBarFill} style={{ width: `${progressBarWidth}%` }} />
      {dotPositions.map((pos, idx) => (
        <Box 
          key={idx} 
          className={`${classes.progressDot} ${idx <= activePhaseIndex ? classes.activeDot : ''}`} 
          style={{ left: `${pos}%` }}
        >
          <Box className={classes.innerDot} />
        </Box>
      ))}
    </Box>
  );
};

const MessageList = ({
  messages,
  currentMessageIndex,
  completedMessageIndexes,
  isInLoopPhase,
  isFeedbackGenerated,
}) => {
  const classes = useStyles();
  const messageListRef = useRef(null);

  useEffect(() => {
    if (messageListRef.current) {
      const offset = currentMessageIndex * 44 - (180 / 2 - 44 / 2);
      messageListRef.current.style.transform = `translateY(${-offset}px)`;
    }
  }, [currentMessageIndex]);

  return (
    <Box className={classes.messageBox}>
      <Box className={classes.messageList} ref={messageListRef}>
        {messages.map((msg, index) => {
          const isActiveMessage = index === currentMessageIndex;
          const isCompleted = completedMessageIndexes.includes(index);
          const isUpcoming = index > currentMessageIndex;
          
          const showCompletedState = !(isInLoopPhase && !isFeedbackGenerated) && isCompleted;
          
          const stateClass = isActiveMessage
            ? classes.activeMessageItem
            : showCompletedState
            ? classes.completedMessageItem
            : '';

          return (
            <Box key={index} className={`${classes.messageItem} ${stateClass}`}>
              {showCompletedState ? (
                <CheckCircleIcon className={classes.checkIcon} />
              ) : isActiveMessage ? (
                <CircularProgress size={24} className={classes.loadingIcon} />
              ) : (
                <Box className={classes.upcomingIcon} />
              )}
              <Typography variant="h6-medium" color="neutral.clr-900">{msg.message}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const AnalysisScreen = ({ isFeedbackGenerated = false, entryPoint }) => {
  const classes = useStyles();
  const navigateWithClient = useNavigateWithClient();
  const location = useLocation();

  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [completedMessageIndexes, setCompletedMessageIndexes] = useState([]);
  const [isInLoopPhase, setIsInLoopPhase] = useState(false);

  const attempt = useCodingStore(state => state.attempt);
  const challenge = useCodingStore(state => state.challenge);
  const analysis = useCodingStore(state => state.analysis);

  const phases = Object.values(AnalysisPhases);
  const activePhase = phases[activePhaseIndex];
  const isFinalizingFeedback = activePhase.name === 'Finalizing Feedback';

  const messages = isFinalizingFeedback
    ? isInLoopPhase
      ? activePhase.loop
      : activePhase.static
    : activePhase.messages;

  useEffect(() => {
    if (currentMessageIndex < messages.length) {
      const currentMessage = messages[currentMessageIndex];
      const duration =
        isFeedbackGenerated && currentMessage.duration !== 1000
          ? 1000
          : currentMessage.duration;

      const timer = setTimeout(() => {
        const lastStatic = isFinalizingFeedback && !isInLoopPhase && 
                            currentMessageIndex === activePhase.static.length - 1;
        const lastLoop = isFinalizingFeedback && isInLoopPhase && 
                          currentMessageIndex === activePhase.loop.length - 1;

        if (!(isInLoopPhase && !isFeedbackGenerated)) {
          setCompletedMessageIndexes((prev) => [...prev, currentMessageIndex]);
        }

        if (lastStatic) {
          setIsInLoopPhase(true);
          setCurrentMessageIndex(0);
          setCompletedMessageIndexes([]);
        } else if (lastLoop) {
          if (isFeedbackGenerated) {
            advancePhase();
          } else {
            restartLoop();
          }
        } else if (!isFinalizingFeedback && currentMessageIndex === messages.length - 1) {
          advancePhase();
        } else {
          setCurrentMessageIndex((prev) => prev + 1);
        }
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex, messages, isFeedbackGenerated]);

  const advancePhase = () => {
    const isLast = activePhaseIndex === phases.length - 1;
    if (isLast) {
      setTimeout(() => 
        // Replace, don't push: the assessment route must not stay behind the
        // report, because going back to it starts - and spends - a new attempt.
        // `entryPoint` tells the report's back button where the run started.
        navigateWithClient(`/performance/4c/${attempt._id}`, {
          state: {
            from: location.pathname,
            entryPoint
          },
          replace: true
        })
      ,1000);
    } else {
      setActivePhaseIndex((prev) => prev + 1);
      setCurrentMessageIndex(0);
      setCompletedMessageIndexes([]);
      setIsInLoopPhase(false);
    }
  };

  const restartLoop = () => {
    setCurrentMessageIndex(0);
    setCompletedMessageIndexes([]);
  };

  return (
    <Box className={classes.root}>
      <video className={classes.backgroundVideo} autoPlay loop muted playsInline>
        <source src="https://assets.languify.in/video/engagementBgVideo.webm" type="video/webm" />
      </video>
      <Box className={classes.container}>
        <Box className={classes.analysisContainer}>
          <img 
            src="https://assets.languify.in/images/Frame+1171277054.png" 
            alt="frame" 
            width={188} 
            style={{ position: 'absolute', top: 0, left: 0 }} 
          />
          <img 
            src="https://assets.languify.in/images/Frame+1171277066.png" 
            alt="frame" 
            width={188} 
            style={{ position: 'absolute', top: 0, right: 0 }} 
          />

          <Box className={classes.phasesContainer}>
            <Box className={classes.topBox}>
              <Phases phases={phases} activePhaseIndex={activePhaseIndex} />
              <ProgressBar activePhaseIndex={activePhaseIndex} />
            </Box>

            <MessageList
              messages={messages}
              currentMessageIndex={currentMessageIndex}
              completedMessageIndexes={completedMessageIndexes}
              isInLoopPhase={isInLoopPhase}
              isFeedbackGenerated={isFeedbackGenerated}
            />
          </Box>
        </Box>

        <Box className={classes.codeContainer}>
          <Typography variant="h4-semiBold" color="neutral.clr-700" sx={{ textAlign: 'center' }}>
            Meanwhile, you can look for optimal solution of this problem
          </Typography>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <img 
              src="https://assets.languify.in/images/Optimal+Solution+1.png" 
              alt="robot" 
              width={99} 
              style={{ position: 'absolute', top: -99, right: 75 }} 
            />
            <CodeReviewCard
              hideHeading
              challenge={challenge}
              analysis={Object.values(analysis)}
              codeSubmissions={Object.values(analysis)}
            />
          </Box>
          <img 
            src="https://assets.languify.in/images/bottomFrameEngagement.png" 
            alt="frame" 
            width={319} 
            style={{ position: 'absolute', bottom: 0, left: 0 }} 
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AnalysisScreen;